import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Building2, GraduationCap, BookOpen } from "lucide-react";

const ROLES = [
  { key: "institute_admin",      icon: Building2,      title: "I'm an institute",         desc: "Coaching, school or trainer — onboard your students" },
  { key: "student",              icon: GraduationCap,  title: "I'm a student (institute)", desc: "I have a join code from my coaching" },
  { key: "self_study_student",   icon: BookOpen,       title: "I'm a self-study student",  desc: "Personal AI tutor — free forever" },
];

export default function Register() {
  const [sp] = useSearchParams();
  const spRole = sp.get("role");
  const refCode = (sp.get("ref") || "").trim().toUpperCase();
  const initialRole = ROLES.some(r => r.key === spRole) ? spRole : (refCode ? "self_study_student" : "institute_admin");
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [instituteName, setInstituteName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [aiAck, setAiAck] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => { setErr(""); }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!aiAck) {
      setErr("Please acknowledge the AI accuracy notice to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = { email, password, name, role,
        mobile: mobile.trim(),
        institute_name: role === "institute_admin" ? instituteName : undefined,
        join_code: role === "student" ? joinCode : undefined,
        referral_code: role === "self_study_student" && refCode ? refCode : undefined };
      const { data } = await http.post("/auth/register", payload);
      await refresh();
      if (data.email_verification_required) {
        navigate("/verify-email");
        return;
      }
      const r = data.user.role;
      navigate(
        r === "institute_admin" ? "/institute" :
        r === "self_study_student" ? "/selfstudy" :
        "/student"
      );
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Create your VidyaAI Study account | AI Tutor for Institutes & Students"
        description="Sign up free — institute admin, institute student or self-study learner. Build your own AI tutor from notes, PDFs and videos. Free Starter tier, no credit card."
        path="/register"
        noindex={true}
      />
      <Navbar variant="auth" />
      <div className="container-x py-12">
        <div className="max-w-xl mx-auto">
          <div className="label-eyebrow mb-4">Get started</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mb-8">Create your account</h1>

          <div className="space-y-2 mb-8" data-testid="role-toggle">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = role === r.key;
              return (
                <button
                  type="button" key={r.key} onClick={() => setRole(r.key)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all flex items-start gap-3 ${active ? "border-[#C24933] bg-[#FCE8E2]" : "border-[#E6E2D8] bg-[#FDFBF7] hover:bg-[#F4F1EB]"}`}
                  data-testid={`role-${r.key}`}
                >
                  <Icon size={22} strokeWidth={1.6} className={active ? "text-[#C24933] mt-0.5" : "text-[#4A4640] mt-0.5"} />
                  <div>
                    <div className="font-display text-base leading-tight">{r.title}</div>
                    <div className="text-xs text-[#7A7369] mt-1">{r.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">Your name</label>
              <input required className="input-field" value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" />
            </div>
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">Email</label>
              <input type="email" required className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email" />
            </div>
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">Mobile</label>
              <input
                type="tel" required inputMode="tel" className="input-field" value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91 98765 43210" data-testid="reg-mobile"
              />
              <p className="text-xs text-[#7A7369] mt-1">Required. Lets you sign in with mobile + password too. We'll never share it.</p>
            </div>
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">Password</label>
              <input type="password" required minLength={8} className="input-field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password" />
              <p className="text-xs text-[#7A7369] mt-1">Min 8 characters, at least 1 letter + 1 digit.</p>
            </div>
            {role === "institute_admin" && (
              <div>
                <label className="block text-sm text-[#4A4640] mb-2">Institute / Coaching name</label>
                <input required className="input-field" value={instituteName} onChange={(e) => setInstituteName(e.target.value)} placeholder="e.g. Sharma Coaching Classes" data-testid="reg-institute-name" />
              </div>
            )}
            {role === "student" && (
              <div>
                <label className="block text-sm text-[#4A4640] mb-2">Institute join code</label>
                <input required className="input-field uppercase tracking-widest" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="ABCD1234" data-testid="reg-join-code" />
                <p className="text-xs text-[#7A7369] mt-2">Ask your coaching for the code.</p>
              </div>
            )}
            {role === "self_study_student" && !refCode && (
              <div>
                <label className="block text-sm text-[#4A4640] mb-2">Referral code (optional)</label>
                <input
                  className="input-field uppercase tracking-widest"
                  placeholder="VID12345"
                  data-testid="reg-referral-code"
                  onChange={(e) => {
                    const v = e.target.value.trim().toUpperCase();
                    if (v) navigate(`/register?role=self_study_student&ref=${v}`, { replace: true });
                  }}
                />
                <p className="text-xs text-[#7A7369] mt-1.5">Have a friend's invite code? Enter it here.</p>
              </div>
            )}
            {role === "self_study_student" && refCode && (
              <div className="rounded-xl bg-[#E6EEE7] border border-[#4A6B53] p-4 text-sm text-[#3D5844]" data-testid="selfstudy-info">
                <strong>🎁 Invited by {refCode}!</strong> Your friend earns rewards when you subscribe later.
              </div>
            )}
            {err && <div className="text-sm text-[#B73B3B]" data-testid="register-error">{err}</div>}
            <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="register-ai-ack-label">
              <input
                type="checkbox"
                required
                checked={aiAck}
                onChange={(e) => setAiAck(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#B5AE9F] text-[#C24933] focus:ring-[#C24933] accent-[#C24933] shrink-0"
                data-testid="register-ai-ack"
              />
              <span className="text-xs text-[#4A4640] leading-snug">
                VidyaAI Study answers from your uploaded notes using AI. While we aim for accuracy, always cross-check important facts with your teacher or textbook before an exam.
              </span>
            </label>
            <button type="submit" disabled={loading || !aiAck} className="btn-primary w-full" data-testid="register-submit">
              {loading ? "Creating…" : role === "self_study_student" ? "Start free" : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-sm text-[#4A4640]">
            Already a user?{" "}
            <Link to="/login" className="text-[#C24933] hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
          <p className="mt-4 text-xs text-[#7A7369]">
            By creating an account you agree to our <Link to="/terms" className="text-[#C24933] hover:underline">Terms of Service</Link>, <Link to="/privacy" className="text-[#C24933] hover:underline">Privacy Policy</Link> and <Link to="/refund-policy" className="text-[#C24933] hover:underline">Refund Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
